import React, { useEffect, useRef, useState } from "react";
import {
  Close as CloseIcon,
  ErrorOutline as ErrorIcon,
  InfoOutlined as InfoIcon,
  ReportProblemOutlined as WarningIcon,
  CheckCircleOutline as SuccessIcon,
} from "@material-ui/icons";
import { shallowEqual, useSelector } from "react-redux";
import { getNotifications } from "../../../api/notification";
import { ROLES } from "../../../utils/constants";
import "./globalNotifications.css";

const POLLING_INTERVAL_MS = 5000;
const TOAST_LIFETIME_MS = 8000;
const MAX_VISIBLE_TOASTS = 3;

const getNotificationClass = (level) => {
  const normalized = String(level || "").toLowerCase();
  if (normalized.includes("error")) return "danger";
  if (normalized.includes("advertencia")) return "warning";
  if (normalized.includes("exito")) return "success";
  return "info";
};

const NotificationIcon = ({ level }) => {
  const type = getNotificationClass(level);
  if (type === "danger") return <ErrorIcon aria-hidden="true" />;
  if (type === "warning") return <WarningIcon aria-hidden="true" />;
  if (type === "success") return <SuccessIcon aria-hidden="true" />;
  return <InfoIcon aria-hidden="true" />;
};

export default function GlobalNotifications() {
  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const accessToken = loggedUser?.accessToken;
  const canReceiveNotifications =
    loggedUser?.role?.rango === ROLES.SUPER_ADMIN ||
    loggedUser?.role?.rango === ROLES.ADMIN;
  const [toasts, setToasts] = useState([]);
  const knownIdsRef = useRef(new Set());
  const timersRef = useRef(new Map());

  const dismiss = (notificationId) => {
    const timer = timersRef.current.get(notificationId);
    if (timer) window.clearTimeout(timer);
    timersRef.current.delete(notificationId);
    setToasts((current) => current.filter((item) => item.id !== notificationId));
  };

  const receive = (notification) => {
    if (!notification?.id || knownIdsRef.current.has(notification.id)) return;

    knownIdsRef.current.add(notification.id);
    // Important messages are persisted alongside operational notices. Refresh the bell immediately.
    window.dispatchEvent(new Event("neo4:messages-changed"));
    if (knownIdsRef.current.size > 300) {
      const ids = Array.from(knownIdsRef.current);
      ids.slice(0, ids.length - 300).forEach((id) => knownIdsRef.current.delete(id));
    }

    // The container is anchored to the bottom, so appending makes new notices grow upward.
    setToasts((current) => [...current, notification].slice(-MAX_VISIBLE_TOASTS));
    const timer = window.setTimeout(
      () => dismiss(notification.id),
      TOAST_LIFETIME_MS
    );
    timersRef.current.set(notification.id, timer);
  };

  useEffect(() => {
    if (!accessToken || !canReceiveNotifications) {
      knownIdsRef.current.clear();
      setToasts([]);
      return undefined;
    }

    let active = true;
    const bootstrap = async () => {
      try {
        const response = await getNotifications(accessToken, 100);
        if (!active) return;
        response.data.forEach((notification) => knownIdsRef.current.add(notification.id));
      } catch {
        // The next polling cycle retries without disrupting the user's current work.
      }
    };

    const poll = async () => {
      try {
        const response = await getNotifications(accessToken, 20);
        if (!active) return;
        response.data.slice().reverse().forEach(receive);
      } catch {
        // Notifications are supplementary; a temporary failure must stay silent.
      }
    };

    bootstrap();
    const intervalId = window.setInterval(poll, POLLING_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [accessToken, canReceiveNotifications]);

  useEffect(() => () => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  if (!canReceiveNotifications) return null;

  return (
    <div className="global-notifications" aria-live="polite" aria-atomic="false">
      {toasts.map((notification) => (
        <article
          className={`global-notification is-${getNotificationClass(notification.nivel)}`}
          key={notification.id}
        >
          <NotificationIcon level={notification.nivel} />
          <div className="global-notification-content">
            <strong>{notification.titulo}</strong>
            <p>{notification.mensaje}</p>
            {notification.agente_nombre && (
              <span>{notification.agente_nombre}</span>
            )}
          </div>
          <button
            aria-label="Cerrar aviso"
            className="global-notification-close"
            onClick={() => dismiss(notification.id)}
            type="button"
          >
            <CloseIcon fontSize="small" />
          </button>
          <span
            aria-hidden="true"
            className="global-notification-progress"
            style={{ animationDuration: `${TOAST_LIFETIME_MS}ms` }}
          />
        </article>
      ))}
    </div>
  );
}
